import { Link, useLocation } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';
import { Plug } from 'lucide-react';
import { usePermissions } from '@/contexts/RoleContext';
import { useDemoMode } from '@/features/demo/useDemoMode';
import { useActiveSource } from '@/features/sources/useActiveSource';
import { WARNING_BANNER_ACTION_CLASS, WarningBanner } from './WarningBanner';

/**
 * Global banner shown on every main-app route when the organization has no
 * source of RECORDS connected. Clicking the CTA jumps straight to
 * `/settings?section=data_source`, where the connection wizard lives.
 *
 * Источник аудио — не источник записей. Телефония, подключённая в роли
 * `audio_source`, отдаёт запись разговора, а сами звонки заводит CRM: если
 * гасить баннер по одному наличию источника, организация с живой телефонией и
 * отвалившейся CRM перестанет получать записи молча. Поэтому баннер остаётся —
 * с текстом, который не врёт в другую сторону: подключено, но записей нет.
 */
export function DataSourceBanner() {
  const { t } = useTranslation(['common', 'settings']);
  const { canManageProjects } = usePermissions();
  const location = useLocation();
  const demo = useDemoMode();
  const { source, isLoading } = useActiveSource();

  // Only show to users who can actually connect a source (owner/admin/client).
  if (!canManageProjects) return null;
  // Hide while loading — avoid flashing the banner on every navigation.
  if (isLoading || demo.isLoading) return null;
  // Demo mode carries its own "connect a source" surfaces (DemoBanner strip /
  // DemoDataCard on the dashboard) — don't stack a second nag on top.
  if (demo.isDemoActive) return null;
  // Источник записей подключён → напоминать не о чем. Источник аудио этот
  // вопрос не закрывает: записи через него не приходят.
  if (source && source.role !== 'audio_source') return null;
  // Already inside /settings — the sidebar there points to Data Source, so
  // suppress the extra nag.
  if (location.pathname.startsWith('/settings')) return null;

  return (
    <WarningBanner
      icon={Plug}
      data-testid="data-source-banner"
      message={
        source
          ? t('common:dataSourceBanner.audioOnlyMessage', {
              source: source.name,
            })
          : t(
              'common:dataSourceBanner.message',
              'Источник данных не подключён — вы не получаете новые записи для анализа.'
            )
      }
      action={
        <Link
          to="/settings"
          search={{ section: 'data_source' }}
          className={WARNING_BANNER_ACTION_CLASS}
        >
          {t('common:dataSourceBanner.cta', 'Подключить источник')}
        </Link>
      }
    />
  );
}
